import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, m as marginPropTypes, p as paddingPropTypes } from './spacing-0f39ba4a.js';
import { g as getZIndex, z as zIndexProps } from './zIndex-60238fbe.js';
import { B as Button } from './Button-e85764bc.js';
import { p as props } from './componentEl-fcbc301e.js';
import { B as Box } from './Box-a46c799c.js';
import useDropdownState from './useDropdownState.js';

/**
Dropdown trigger component, for more control
*/
function DropdownTrigger({
  componentEl: Component,
  className,
  margin,
  children,
  padding,
  onClick,
  open,
  fullHeight,
  fullWidth,
  ...otherProps
}) {
  let cssClasses = ['st_dropdown__trigger'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  if (fullHeight) cssClasses.push('st_height--full');
  if (fullWidth) cssClasses.push('st_width--full');
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    onClick: onClick,
    "aria-haspopup": "true",
    "aria-expanded": !!open
  }, otherProps), children);
}
DropdownTrigger.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for box, if any */
  margin: marginPropTypes,
  /** whether dropdown is open */
  open: PropTypes.bool,
  /** padding for content */
  padding: paddingPropTypes,
  /** event listener for when trigger button is clicked */
  onClick: PropTypes.func,
  /** whether button takes up all available horizontal space */
  fullWidth: PropTypes.bool,
  /** whether button takes up all available vertical space */
  fullHeight: PropTypes.bool
};
DropdownTrigger.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  open: null,
  padding: null,
  onClick: null,
  fullWidth: false,
  fullHeight: false
};
DropdownTrigger.displayName = 'Dropdown.Trigger';

/**
Dropdown content component, for more control
*/
function DropdownContent({
  arrow,
  className,
  margin,
  children,
  position,
  padding,
  open,
  zIndex,
  ...otherProps
}) {
  if (!open) return null;
  let cssClasses = ['st_dropdown__content', `st_dropdown__content--${position}`];
  if (zIndex) cssClasses.push(getZIndex(zIndex));
  if (arrow) cssClasses.push(`st_box--arrow st_box--arrow-${arrow}`);
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Box, _extends({
    className: cssClasses.join(' '),
    padding: padding,
    margin: margin
  }, otherProps), children);
}
DropdownContent.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for box, if any */
  margin: marginPropTypes,
  /** position of dropdown content */
  position: PropTypes.oneOf(['left', 'right', 'left-flush', 'right-flush', 'none', 'none-horizontal', 'center']),
  /** display a pointy arrow on one of the side */
  arrow: PropTypes.oneOf(['top', 'top-left', 'top-right']),
  /** padding for content */
  padding: paddingPropTypes,
  /** whether dropdown is open */
  open: PropTypes.bool,
  /** z-index for dropdown when it's open. One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps
};
DropdownContent.defaultProps = {
  arrow: null,
  className: null,
  margin: null,
  position: null,
  padding: null,
  open: null,
  zIndex: 'minor'
};
DropdownContent.displayName = 'Dropdown.Content';

/**
Dropdown without state, for when it needs to be controlled from outside
*/
function DropdownDumb({
  arrow,
  className,
  margin,
  componentRef,
  open,
  color,
  trigger,
  children,
  position,
  disabled,
  padding,
  onToggle,
  zIndex,
  fullHeight,
  ...otherProps
}) {
  let cssClasses = ['st_dropdown'];
  if (open) cssClasses.push('st_dropdown--open');
  if (fullHeight) cssClasses.push('st_height--full');
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  if (trigger) {
    return /*#__PURE__*/React.createElement("div", _extends({
      className: cssClasses.join(' ')
    }, otherProps, {
      ref: componentRef
    }), /*#__PURE__*/React.createElement(DropdownTrigger, {
      componentEl: Button,
      tertiary: true,
      color: color,
      onClick: onToggle,
      "aria-haspopup": "true",
      "aria-expanded": !!open,
      disabled: disabled,
      active: !!open
    }, trigger), open && /*#__PURE__*/React.createElement(DropdownContent, {
      arrow: arrow,
      open: open,
      position: position,
      padding: padding,
      zIndex: zIndex
    }, children));
  }

  // if trigger prop is missing, we are in subcomponent land
  // attach props to Dropdown.Trigger and Dropdown.Content
  // which should both exist for this mode to work
  const requiredComponents = ['Dropdown.Trigger', 'Dropdown.Content'];
  const childrenWithProps = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    const cloneParams = {};
    if (child.type.displayName === 'Dropdown.Trigger') {
      cloneParams.open = open;
      cloneParams.disabled = child.props.disabled || disabled;
      cloneParams.onClick = onToggle;
      requiredComponents.splice(requiredComponents.indexOf(child.type.displayName), 1);
    }
    if (child.type.displayName === 'Dropdown.Content') {
      cloneParams.open = open;
      cloneParams.padding = child.props.padding || padding;
      cloneParams.position = child.props.position || position;
      requiredComponents.splice(requiredComponents.indexOf(child.type.displayName), 1);
    }
    return /*#__PURE__*/React.cloneElement(child, cloneParams);
  });
  if (requiredComponents.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`
      The Dropdown component can't render because instances of ${requiredComponents.join(', ')} are missing in the children
    `);
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps, {
    ref: componentRef
  }), childrenWithProps);
}
DropdownDumb.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for box, if any */
  margin: marginPropTypes,
  /** whether dropdown is open */
  open: PropTypes.bool,
  /** label for dropdown trigger.
  Omit to have full control via Dropdown.Trigger + Dropdown.Content */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** position of dropdown content */
  position: PropTypes.oneOf(['left', 'right', 'left-flush', 'right-flush', 'none', 'none-horizontal', 'center']),
  /** display a pointy arrow on one of the side */
  arrow: PropTypes.oneOf(['top-left', 'top-right', 'top']),
  /** color of dropdown trigger */
  color: PropTypes.oneOf(['blurple', 'gray']),
  /** padding for content. Overrides default */
  padding: paddingPropTypes,
  /** whether control should be disabled */
  disabled: PropTypes.bool,
  /** event listener for when dropdown is toggled; passes open state as parameter */
  onToggle: PropTypes.func,
  /** ref for dropdown component */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** z-index for dropdown when it's open. One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps,
  /** whether container element should have height 100% */
  fullHeight: PropTypes.bool
};
DropdownDumb.defaultProps = {
  arrow: null,
  className: null,
  margin: null,
  open: false,
  position: 'left',
  color: 'blurple',
  padding: {
    vertical: 'smd',
    horizontal: 'md'
  },
  trigger: null,
  disabled: false,
  onToggle: () => null,
  componentRef: null,
  zIndex: 'minor',
  fullHeight: false
};

/**
Smart dropdown, that handles its own state management
*/
function DropdownSmart({
  open: openProp,
  children,
  onToggle,
  ...otherProps
}) {
  const {
    open,
    componentRef,
    toggleDropdown
  } = useDropdownState({
    open: openProp,
    onToggle
  });
  return /*#__PURE__*/React.createElement(DropdownDumb, _extends({
    open: open,
    "aria-expanded": !!open
  }, otherProps, {
    componentRef: componentRef,
    onToggle: toggleDropdown
  }), children);
}
DropdownSmart.propTypes = {
  /** whether dropdown is open on initial render */
  open: PropTypes.bool,
  /** event listener for when dropdown is toggled; passes open state as parameter */
  onToggle: PropTypes.func
};
DropdownSmart.defaultProps = {
  onToggle: () => null,
  open: false
};

function Dropdown({
  arrow,
  className,
  margin,
  open,
  defaultOpen,
  color,
  trigger,
  children,
  position,
  disabled,
  padding,
  onToggle,
  zIndex,
  fullHeight,
  ...otherProps
}) {
  const allProps = {
    arrow,
    className,
    margin,
    color,
    trigger,
    position,
    disabled,
    onToggle,
    padding,
    children,
    zIndex,
    fullHeight,
    ...otherProps
  };
  if (open === null) {
    return /*#__PURE__*/React.createElement(DropdownSmart, _extends({
      open: defaultOpen
    }, allProps));
  }
  return /*#__PURE__*/React.createElement(DropdownDumb, _extends({
    open: open
  }, allProps));
}
Dropdown.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for box, if any */
  margin: marginPropTypes,
  /** whether dropdown is open. Use for uncontrolled dropdown */
  open: PropTypes.bool,
  /** whether dropdown is initially open. Use for controlled component */
  defaultOpen: PropTypes.bool,
  /** label for dropdown trigger */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** position of dropdown content */
  position: PropTypes.oneOf(['left', 'right', 'left-flush', 'right-flush', 'none', 'none-horizontal', 'center']),
  /** display a pointy arrow on one of the side */
  arrow: PropTypes.oneOf(['top-left', 'top-right', 'top']),
  /** color of dropdown trigger */
  color: PropTypes.oneOf(['blurple', 'gray']),
  /** padding for content. Setting this overrides defaults and does not extend them */
  padding: paddingPropTypes,
  /** whether control should be disabled */
  disabled: PropTypes.bool,
  /** event listener for when dropdown is toggled; passes open state when uncontrolled */
  onToggle: PropTypes.func,
  /** z-index for dropdown when it's open. One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps,
  /** whether container element should have height: 100% */
  fullHeight: PropTypes.bool
};
Dropdown.defaultProps = {
  arrow: null,
  className: null,
  margin: null,
  open: null,
  defaultOpen: null,
  position: 'left',
  color: 'blurple',
  padding: {
    vertical: 'smd',
    horizontal: 'md'
  },
  disabled: false,
  onToggle: () => null,
  trigger: null,
  zIndex: 'minor',
  fullHeight: false
};
Dropdown.Trigger = DropdownTrigger;
Dropdown.Content = DropdownContent;

export { Dropdown as D };
